import React, { useState, useEffect, useCallback } from 'react'
import { fetchPitches, fetchStaysForPitch, fetchClients, fetchGuestInventories, isStaffInventoryDone, addRecentLocation } from './fieldApi'
import { findPitchByLocation } from './fieldSearch'
import { proposeStay, neighborStays } from './fieldStays'
import { nowSeconds } from './fieldDates'
import FieldPitchSearch from './FieldPitchSearch'
import FieldStayNumber from './FieldStayNumber'
import FieldStayProposal from './FieldStayProposal'
import FieldStayNeighbors from './FieldStayNeighbors'

// Début d'un inventaire ponctuel : emplacement, puis séjour pour une entrée ou une sortie (lot 2).
// Hors séjour : l'emplacement suffit (décision 3). À la fin, onDone({ type, pitch, stay, client }).

// Liste des emplacements gardée le temps de la session : elle bouge rarement
let pitchesCache = null

function latestGuestInventory(inventories)
{
	if (!inventories || inventories.length === 0)
		return null
	return inventories.slice().sort((a, b) => (b.create_time || 0) - (a.create_time || 0))[0]
}

export default function FieldStart({ type, onExit, onDone, scrollToTop })
{
	const [step, setStep] = useState('pitch')
	const [proposalStep, setProposalStep] = useState('proposal')
	const [pitches, setPitches] = useState(pitchesCache)
	const [pitchesError, setPitchesError] = useState(false)
	const [pitch, setPitch] = useState(null)
	const [stays, setStays] = useState([])
	const [staysStatus, setStaysStatus] = useState('loading')
	const [clients, setClients] = useState({})
	const [proposed, setProposed] = useState(null)
	const [chosen, setChosen] = useState(null)
	const [guestInventory, setGuestInventory] = useState(null)
	const [now] = useState(nowSeconds())
	const withStay = type === 'arrival' || type === 'departure'

	const loadPitches = useCallback(() => {
		setPitchesError(false)
		fetchPitches()
			.then(list => {
				pitchesCache = list
				setPitches(list)
			})
			.catch(() => setPitchesError(true))
	}, [])

	useEffect(() => {
		if (!pitchesCache)
			loadPitches()
	}, [loadPitches])

	useEffect(() => {
		if (scrollToTop)
			scrollToTop()
	}, [step, scrollToTop])

	// Charge les séjours de l'emplacement et calcule le séjour proposé (décision 16)
	const loadStays = useCallback((targetPitch, forcedStay) => {
		setStaysStatus('loading')
		setGuestInventory(null)
		fetchStaysForPitch(targetPitch.location)
			.then(list => {
				const stay = forcedStay || proposeStay(list, type, now, isStaffInventoryDone)
				return Promise.all([
					fetchClients(forcedStay ? list.concat([forcedStay]) : list),
					stay ? fetchGuestInventories(stay.stay_number) : Promise.resolve([])
				])
					.then(([byNumber, inventories]) => {
						setStays(list)
						setClients(byNumber)
						setProposed(forcedStay ? null : stay)
						setChosen(stay)
						setGuestInventory(latestGuestInventory(inventories))
						setStaysStatus('ready')
					})
			})
			.catch(() => setStaysStatus('error'))
	}, [type, now])

	const selectPitch = (selectedPitch) => {
		addRecentLocation(selectedPitch.location)
		setPitch(selectedPitch)
		if (!withStay)
		{
			onDone({ type, pitch: selectedPitch, stay: null, client: null })
			return ;
		}
		setProposalStep('proposal')
		setStep('proposal')
		loadStays(selectedPitch, null)
	}

	// Séjour trouvé par son numéro : on retrouve son emplacement, puis on le montre pour confirmation
	const selectStayByNumber = (stay) => {
		const found = findPitchByLocation(pitches || [], stay.location)
		const stayPitch = found || { location: stay.location || '', location_category: stay.location_category || '', type: '' }
		if (found)
			addRecentLocation(found.location)
		setPitch(stayPitch)
		setProposalStep('proposal-manual')
		setStep('proposal-manual')
		loadStays(stayPitch, stay)
	}

	const confirm = (stay) => {
		onDone({ type, pitch, stay, client: stay ? clients[stay.client_number] || null : null })
	}

	if (step === 'stay-number')
	{
		return (
			<FieldStayNumber
				type={type}
				onSelect={selectStayByNumber}
				onPitchSearch={() => setStep('pitch')}
				onBack={onExit}
			/>
		)
	}

	if ((step === 'proposal' || step === 'proposal-manual') && pitch)
	{
		return (
			<FieldStayProposal
				type={type}
				pitch={pitch}
				status={staysStatus}
				stay={chosen}
				client={chosen ? clients[chosen.client_number] : null}
				guestInventory={guestInventory}
				manual={step === 'proposal-manual'}
				hasOtherStays={stays.length > 0}
				now={now}
				onConfirm={() => confirm(chosen)}
				onOtherStays={() => setStep('neighbors')}
				onStayNumber={() => setStep('stay-number')}
				onRetry={() => loadStays(pitch, step === 'proposal-manual' ? chosen : null)}
				onBack={() => setStep(step === 'proposal-manual' ? 'stay-number' : 'pitch')}
			/>
		)
	}

	if (step === 'neighbors' && pitch)
	{
		return (
			<FieldStayNeighbors
				type={type}
				pitch={pitch}
				proposed={chosen}
				neighbors={neighborStays(stays, chosen, now)}
				clients={clients}
				now={now}
				isDone={isStaffInventoryDone}
				onConfirm={confirm}
				onStayNumber={() => setStep('stay-number')}
				proposedTitle={proposalStep === 'proposal-manual' ? 'Séjour choisi' : 'Séjour proposé'}
				backLabel={chosen ? (proposalStep === 'proposal-manual' ? 'Séjour choisi' : 'Séjour proposé') : 'Retour'}
				onBack={() => setStep(proposalStep)}
			/>
		)
	}

	return (
		<FieldPitchSearch
			type={type}
			pitches={pitches}
			loadError={pitchesError}
			onRetry={loadPitches}
			onSelect={selectPitch}
			onStayNumber={withStay ? () => setStep('stay-number') : null}
			onBack={onExit}
		/>
	)
}
